<script setup lang="ts">
import { watch, type PropType, type Ref } from 'vue'
import { onMounted } from 'vue'
import { ref } from 'vue'
import { nextTick } from 'vue'
import { computed } from 'vue'
import { createPopper, type BasePlacement } from '@popperjs/core'
import { vClickOutside } from '@/directives'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  wrapperRef: {
    type: Object as PropType<HTMLElement | null>,
    default: null
  },
  placement: {
    type: String,
    default: 'bottom',
    required: false
  },
  arrow: {
    type: Boolean,
    required: false,
    default: true
  },
  interceptClickOutside: {
    type: Boolean,
    required: false,
    default: false
  },
  zIndex: {
    type: Number,
    required: false,
    default: 10001
  },
  preventOverflow: {
    type: Boolean,
    required: false,
    default: true
  },
  overflowPadding: {
    type: Number,
    required: false,
    default: 15
  },
  style: {
    type: String,
    required: false,
    default: ''
  },
  tipClass: {
    type: String,
    required: false,
    default: ''
  },
  boxShadow: {
    type: Boolean,
    required: false,
    default: true
  },
  strategy: {
    type: String as PropType<'absolute' | 'fixed'>,
    required: false,
    default: 'absolute'
  }
})

let droppedDown = ref(props.modelValue)

let popperRef: Ref<HTMLElement | null> = ref(null)
let wrapperRef = computed(() => {
  return props.wrapperRef as HTMLElement
})

const interceptOutside = computed(() => props.interceptClickOutside)

function calcFallbakPlacement(placement: string): string[] {
  switch (placement) {
    case 'top':
      return ['bottom']
    case 'bottom':
      return ['top']
    case 'left':
      return ['right']
    case 'right':
      return ['left']
  }
  return [placement]
}

const emit = defineEmits(['update:modelValue', 'close'])

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      initPopper()
    }
    droppedDown.value = value
  }
)

watch(droppedDown, (newVal) => {
  if (newVal) {
    nextTick(() => {
      popper?.update() // 动画开始前更新定位
    })
  }
})

function handleClose() {
  console.log('!debug!触发关闭')
  if (!interceptOutside.value) {
    droppedDown.value = false
    emit('close')
    emit('update:modelValue', false)
  }
}

// impelement by: https://popper.js.org/
let popper

function initPopper() {
  nextTick(() => {
    if (wrapperRef.value != null && popperRef.value != null) {
      const modifiers = [
        {
          name: 'zIndex',
          enabled: true,
          options: {
            zIndex: props.zIndex // 设置所需的 z-index 值
          }
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: calcFallbakPlacement(props.placement),
            padding: 6
          }
        },
        {
          name: 'offset',
          options: {
            offset: [0, 10]
          }
        }
      ]

      if (props.preventOverflow) {
        modifiers.push({
          name: 'preventOverflow',
          options: {
            padding: props.overflowPadding // 距离视口边缘的边距
          }
        } as any)
      }

      popper = createPopper(wrapperRef?.value, popperRef?.value, {
        placement: props.placement as BasePlacement,
        strategy: props.strategy as 'absolute' | 'fixed',
        modifiers
      })
    }
  })
}

onMounted(() => {
  initPopper()
})
</script>

<template>
  <teleport to="body">
    <div
      v-if="droppedDown"
      ref="popperRef"
      drop-shadow-md
      v-click-outside:popperRef="handleClose"
      font-normal
      v-bind="$attrs"
      :class="[
        'margin-wrapper',
        props.tipClass ? props.tipClass : 'bg-base',
        props.arrow ? 'tooltip' : 'tooltip-plain'
      ]"
      :style="{
        backgroundColor: props.style,
        zIndex: props.zIndex,
        boxShadow: props.boxShadow ? '0px 0px 12px #dddddd' : 'none'
      }">
      <div v-if="props.arrow" id="arrow" data-popper-arrow></div>
      <slot></slot>
    </div>
  </teleport>
</template>
<style lang="scss" scoped>
.tooltip {
  font-weight: bold;
  padding: 0px 0px;
  font-size: 13px;
  border-radius: 0.25rem;
  // box-shadow: 0px 0px 12px #dddddd;
  z-index: 10000;

  #arrow,
  #arrow::before {
    position: absolute;
    width: 8px;
    height: 8px;
    background: inherit;
  }

  #arrow {
    visibility: hidden;
  }

  #arrow::before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
  }
}

.tooltip[data-popper-placement^='top'] > #arrow {
  bottom: -4px;
}

.tooltip[data-popper-placement^='bottom'] > #arrow {
  top: -4px;
}

.tooltip[data-popper-placement^='left'] > #arrow {
  right: -4px;
}

.tooltip[data-popper-placement^='right'] > #arrow {
  left: -4px;
}

.tooltip-plain {
  padding: 0px 0px;
  border-radius: 0.25rem;
  box-shadow: 0px 0px 12px #dddddd;
  z-index: 10000;
  @apply bg-base;
}

.margin-wrapper {
  margin: 0px !important;
  z-index: 2000;
  transition: height 2s ease-in-out !important;
}
</style>
